// React and MUI imports for hooks, layout, and UI
import React, { useEffect } from 'react';
import {
  Box, // MUI layout component
  ThemeProvider, // (not used, can be removed)
  Typography, // MUI text component
  Container, // MUI container for responsive width
  useMediaQuery, // MUI hook for responsive design
  useTheme, // MUI hook for theme access
} from '@mui/material';
import { CssBaseline, styled } from '@mui/material'; // CssBaseline for CSS reset, styled for custom components
import Header from '../../commons/Header'; // App header
import Footer from '../../commons/Footer'; // App footer
import HDButton from '../../hd-ui-kit/HDButton'; // Custom button
import { useNavigate } from 'react-router'; // React Router navigation
import ArrowForwardSharpIcon from '@mui/icons-material/ArrowForwardSharp'; // Arrow icon
import Api from '../../Api'; // API utility for backend calls

/**
 * HomePage
 * Landing page for Heidrick for Leaders, describing the benefits of creating a profile.
 * Shows feature highlights, sign up and sign in options, and adapts layout for mobile/tablet/desktop.
 */
const HomePage = () => {
  // (themeMode is unused, can be removed)
  // Access the current MUI theme object
  const theme = useTheme();
  // Determine if the screen is mobile or tablet size for responsive layout
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  // React Router navigation function
  const navigate = useNavigate();

  // Style for the main title
  const TitleStyle = {
    fontFamily: 'Aventa',
    fontStyle: 'normal',
    fontWeight: 500,
    fontSize: isMobile || isTablet ? '31px' : '41px',
    lineHeight: isMobile || isTablet ? '41px' : '54px',
    color: theme.palette.mode == 'light' ? '#002136' : '#FFFFFF',
    textAlign: 'left',
  };

  // Style for main vertical flex box
  const MainStyledBox = {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '5px',
  };

  // Style for feature highlight text
  const StyledText = {
    fontFamily: 'Aventa',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '22px',
    lineHeight: '32px',
    color: theme.palette.mode == 'light' ? '#002136' : '#FFFFFF',
    textAlign: 'left',
  };

  // Style for feature description text
  const StyledTextTwo = {
    fontFamily: 'Aventa',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '22px',
    lineHeight: '32px',
    color: theme.palette.mode == 'light' ? '#334255' : '#CCCCCC',
    textAlign: 'left',
  };

  // Style for the sign up section (row with text and button)
  const StyledSignUp = {
    display: 'flex',
    marginTop: '10px',
    alignItems: 'center',
    paddingBottom: '20px',
  };

  // Style for the sign up button
  const StyledSignUpBtn = {
    fontFamily: 'Aventa',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.mode == 'light' ? '#18768C' : '#66FCFC',
    padding: '0px',
  };

  // Style for the 'Already have an account?' text
  const TitleStyleHaveAccount = {
    fontFamily: 'Aventa',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.mode == 'light' ? '#002136' : '#CCCCCC',
    textAlign: 'left',
  };

  // Style for the left divider (desktop only)
  const StyledDividerLeft = {
    borderLeft:
      isMobile || isTablet
        ? ' '
        : theme.palette.mode == 'light'
          ? '1px solid rgba(0, 33, 54, 0.2)'
          : '1px solid  rgba(255, 255, 255, 0.2)',
    paddingLeft: isMobile || isTablet ? ' ' : '40px',
    marginTop: '20px',
    width: isMobile || isTablet ? '100%' : '50%',
  };

  // Styled arrow icon for the create profile button
  const ArrowForwardSharpIconStyled = styled(ArrowForwardSharpIcon)(() => ({
    color: theme.palette.mode == 'light' ? '#66FCFC' : '#18768C',
  }));

  // Style for the create profile button
  const StyledSignIn = {
    backgroundColor: theme.palette.mode == 'light' ? '#334255' : '#F1F1F1',
    color: theme.palette.mode == 'light' ? '#FFFFFF' : '#002136',
    marginTop: '20px',
    padding: '6px 16px',
    marginBottom: '20px',
  };

  // Style for the wrapper around the create profile button
  const StyledBtn = {
    display: 'flex',
    paddingTop: '12px',
  };

  // Style for the main wrapper, with background image based on theme and device
  const StyledBoxMainWrapper = {
    background:
      theme.palette.mode == 'light'
        ? isMobile || isTablet
          ? 'url(res/homeBGLightMobile.svg) no-repeat top'
          : 'url(res/homeBGLight.svg) no-repeat top'
        : isMobile || isTablet
          ? 'url(res/homeBGDarkMobile.svg) no-repeat top'
          : 'url(res/homeBGDark.svg) no-repeat top',
    backgroundPosition: isMobile || isTablet ? 'right bottom' : 'right 0px',
    height: isMobile ? '1100px' : isTablet ? '1000px' : 'auto',
    backgroundSize: isTablet ? '80%' : 'unset',
  };

  // Style for the wrapper around each feature section
  const StyledDivWrapper = {
    paddingTop: '30px',
  };

  // On mount, redirect to /home if already authorized (user is logged in)
  useEffect(() => {
    let isAuth = Api.isAuthorized();
    if (isAuth) navigate('/home');
  }, []);

  return (
    <>
      {/* CssBaseline normalizes browser styles for consistency */}
      <CssBaseline />
      {/* App header (logo, nav, etc.) */}
      <Header />
      {/* Main background section with theme-based color/gradient and feature highlights */}
      <Box sx={StyledBoxMainWrapper}>
        <Container maxWidth="lg">
          {/* Optional left divider for layout aesthetics */}
          <Box sx={StyledDividerLeft}>
            {/* Main title for the landing page */}
            <Box sx={MainStyledBox}>
              <Typography variant="h2" sx={TitleStyle}>
                Welcome to Heidrick for Leaders. By creating a profile, you can:
              </Typography>
            </Box>
            {/* Feature: Submit your resume */}
            <Box sx={StyledDivWrapper}>
              <Box sx={MainStyledBox}>
                <Typography variant="h2" sx={StyledText}>
                  Submit your resume
                </Typography>
              </Box>
              <Box sx={MainStyledBox}>
                <Typography variant="h2" sx={StyledTextTwo}>
                  Add your resume into the Heidrick system for search consideration.
                </Typography>
              </Box>
            </Box>
            {/* Feature: Manage preferences */}
            <Box sx={StyledDivWrapper}>
              <Box sx={MainStyledBox}>
                <Typography variant="h2" sx={StyledText}>
                  Manage preferences for your future
                </Typography>
              </Box>
              <Box sx={MainStyledBox}>
                <Typography variant="h2" sx={StyledTextTwo}>
                  Let us know what you’re interested in, so we can provide helpful guidance.
                </Typography>
              </Box>
            </Box>
            {/* Feature: Learn where you need to grow */}
            <Box sx={StyledDivWrapper}>
              <Box sx={MainStyledBox}>
                <Typography variant="h2" sx={StyledText}>
                  Learn where you need to grow
                </Typography>
              </Box>
              <Box sx={MainStyledBox}>
                <Typography variant="h2" sx={StyledTextTwo}>
                  See how your experience and Heidrick capabilities compare to accomplished leaders.
                </Typography>
              </Box>
            </Box>
            {/* Create profile button */}
            <Box sx={StyledBtn}>
              <HDButton
                variant={'contained'}
                sx={StyledSignIn}
                endIcon={<ArrowForwardSharpIconStyled />}
                text={'Create your profile'}
                onClick={() => navigate('/register')}
                showAnimation={true}
              />
            </Box>
            {/* Sign in section for existing users */}
            <Box sx={StyledSignUp}>
              <Box>
                <Typography variant="h2" sx={TitleStyleHaveAccount}>
                  Already have an account?
                </Typography>
              </Box>
              <Box>
                <HDButton
                  sx={StyledSignUpBtn}
                  variant={'text'}
                  text={'Sign In'}
                  onClick={() => navigate('/login')}
                />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
      {/* App footer (copyright, links, etc.) */}
      <Footer />
    </>
  );
};

export default HomePage;
